import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

// Fetches the document as a blob so we can send Authorization header, then previews.
export default function DocPreviewDialog({ doc, open, onOpenChange }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !doc) return;
    let revoke;
    setLoading(true);
    api.get(`/documents/${doc.id}`, { responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        setBlobUrl(url);
        revoke = url;
      })
      .catch(() => setBlobUrl(null))
      .finally(() => setLoading(false));
    return () => { if (revoke) URL.revokeObjectURL(revoke); setBlobUrl(null); };
  }, [open, doc]);

  if (!doc) return null;
  const isImage = (doc.content_type || "").startsWith("image/");
  const isPdf = (doc.content_type || "") === "application/pdf";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl p-0 overflow-hidden" data-testid="doc-preview-dialog">
        <DialogHeader className="p-4 pb-3 border-b">
          <DialogTitle className="font-display text-xl flex items-center justify-between gap-4">
            <span className="truncate">{doc.filename}</span>
            <a href={`${API}/documents/${doc.id}`} target="_blank" rel="noreferrer" download className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700 hover:text-sky-900" data-testid="doc-download-link">
              <Download size={14} /> Download
            </a>
          </DialogTitle>
        </DialogHeader>
        <div className="bg-slate-100 min-h-[60vh] flex items-center justify-center" data-testid="doc-preview-body">
          {loading && <p className="text-slate-400 text-sm">Loading…</p>}
          {!loading && blobUrl && isImage && (
            <img src={blobUrl} alt={doc.filename} className="max-h-[70vh] w-auto object-contain" data-testid="doc-preview-image" />
          )}
          {!loading && blobUrl && isPdf && (
            <iframe src={blobUrl} title={doc.filename} className="w-full h-[70vh]" data-testid="doc-preview-pdf" />
          )}
          {!loading && blobUrl && !isImage && !isPdf && (
            <div className="p-8 text-center text-slate-500 text-sm">
              <p>No inline preview available for this file type.</p>
              <p className="mt-1"><a href={`${API}/documents/${doc.id}`} target="_blank" rel="noreferrer" className="text-sky-700 font-semibold">Download to view</a></p>
            </div>
          )}
          {!loading && !blobUrl && <p className="text-slate-400 text-sm">Preview unavailable.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
